import { useRef, useState, useEffect } from 'react';
import { useParams, NavLink, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Printer, Calendar, User, MapPin, X, Ban, Clock, ClipboardCheck, BarChart2, ChevronDown, FileText, List } from 'lucide-react';
import { estimatesApi } from '@/api/client';
import { formatCurrency, type EstimateStatus } from '@medical-counsellor/shared';
import { PrintableEstimate } from '@/components/estimates/PrintableEstimate';
import { VarianceAnalysis } from './VarianceAnalysis';

export function EstimateDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [showDeclineModal, setShowDeclineModal] = useState(false);
    const [declineReason, setDeclineReason] = useState('');
    const [activeTab, setActiveTab] = useState<'details' | 'variance'>('details');
    const [printMode, setPrintMode] = useState<'summary' | 'detailed'>('summary');
    const [isPrintMenuOpen, setIsPrintMenuOpen] = useState(false);
    const printableRef = useRef<HTMLDivElement>(null);
    const printMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (printMenuRef.current && !printMenuRef.current.contains(event.target as Node)) {
                setIsPrintMenuOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const { data: estimate, isLoading, error } = useQuery({
        queryKey: ['estimate', id],
        queryFn: () => estimatesApi.getById(id!),
        enabled: !!id,
    });

    const updateStatusMutation = useMutation({
        mutationFn: ({ status, declineReason }: { status: EstimateStatus; declineReason?: string }) =>
            estimatesApi.updateStatus(id!, { status, declineReason }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['estimate', id] });
            queryClient.invalidateQueries({ queryKey: ['estimates'] });
            setShowDeclineModal(false);
            setDeclineReason('');
        },
    });

    const convertMutation = useMutation({
        mutationFn: () => estimatesApi.convertToActual(id!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['estimate', id] });
            queryClient.invalidateQueries({ queryKey: ['estimates'] });
            navigate(`/estimates/${id}/actual`);
        },
    });

    // Use location's default spacing or fallback to 100
    const printTopSpacing = estimate?.location?.printHeaderSpacing ?? 100;
    const printBottomSpacing = estimate?.location?.printFooterSpacing ?? 100;

    // Get status badge styling
    const getStatusBadge = (status: EstimateStatus) => {
        const styles = {
            PENDING: 'bg-blue-100 text-blue-800 border-blue-200',
            CONVERTED: 'bg-green-100 text-green-800 border-green-200',
            EXPIRED: 'bg-gray-100 text-gray-800 border-gray-200',
            DECLINED: 'bg-red-100 text-red-800 border-red-200',
            CANCELLED: 'bg-orange-100 text-orange-800 border-orange-200',
        };
        return styles[status] || 'bg-gray-100 text-gray-800 border-gray-200';
    };

    // Calculate days until expiry
    const getDaysUntilExpiry = (expiresAt: Date | null) => {
        if (!expiresAt) return null;
        const now = new Date();
        const expiry = new Date(expiresAt);
        const diffTime = expiry.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const handleDecline = () => {
        setShowDeclineModal(true);
    };

    const handleCancel = () => {
        if (confirm('Are you sure you want to cancel this estimate?')) {
            updateStatusMutation.mutate({ status: 'CANCELLED' });
        }
    };

    const handleDeclineSubmit = () => {
        updateStatusMutation.mutate({
            status: 'DECLINED',
            declineReason: declineReason.trim() || undefined,
        });
    };

    const handleConvert = () => {
        if (confirm('Mark this estimate as converted? This will allow you to record actual bill details.')) {
            convertMutation.mutate();
        }
    };

    if (isLoading) {
        return (
            <div className="animate-fade-in">
                <div className="animate-pulse space-y-6">
                    <div className="h-8 bg-slate-200 rounded w-48" />
                    <div className="card">
                        <div className="card-body space-y-4">
                            <div className="h-6 bg-slate-200 rounded w-1/3" />
                            <div className="h-4 bg-slate-200 rounded w-1/2" />
                            <div className="h-4 bg-slate-200 rounded w-2/3" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !estimate) {
        return (
            <div className="animate-fade-in text-center py-16">
                <h2 className="text-xl font-semibold text-slate-900">Estimate not found</h2>
                <NavLink to="/estimates" className="btn btn-primary btn-md mt-4">
                    Back to Estimates
                </NavLink>
            </div>
        );
    }

    // Group lines by section
    const sections = estimate.lines?.reduce((acc: Record<string, any[]>, line: any) => {
        if (!acc[line.sectionName]) {
            acc[line.sectionName] = [];
        }
        acc[line.sectionName].push(line);
        return acc;
    }, {}) || {};

    const handlePrint = (mode: 'summary' | 'detailed') => {
        setPrintMode(mode);
        // Wait for state update to render
        setTimeout(() => {
            window.print();
        }, 100);
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <NavLink
                        to="/estimates"
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </NavLink>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold text-slate-900">
                                {estimate.referenceNumber}
                            </h1>
                            <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadge(
                                    estimate.status
                                )}`}
                            >
                                {estimate.status === 'CONVERTED' ? 'ADMITTED' : estimate.status}
                            </span>
                        </div>
                        <p className="text-slate-600 mt-1">Estimate Details</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {['PENDING', 'EXPIRED', 'DECLINED'].includes(estimate.status) && (
                        <>
                            <button
                                onClick={handleConvert}
                                className="btn btn-success btn-md"
                                disabled={convertMutation.isPending || updateStatusMutation.isPending}
                            >
                                <ClipboardCheck className="w-4 h-4" />
                                Convert & Record Actual
                            </button>
                            {estimate.status === 'PENDING' && (
                                <>
                                    <button
                                        onClick={handleDecline}
                                        className="btn btn-warning btn-md"
                                        disabled={updateStatusMutation.isPending}
                                    >
                                        <X className="w-4 h-4" />
                                        Decline
                                    </button>
                                    <button
                                        onClick={handleCancel}
                                        className="btn btn-danger btn-md"
                                        disabled={updateStatusMutation.isPending}
                                    >
                                        <Ban className="w-4 h-4" />
                                        Cancel
                                    </button>
                                </>
                            )}
                        </>
                    )}

                    {estimate.status === 'CONVERTED' && (
                        <button
                            onClick={() => navigate(`/estimates/${id}/actual`)}
                            className="btn btn-outline btn-md border-slate-300 hover:border-slate-400"
                        >
                            <ClipboardCheck className="w-4 h-4 text-slate-600" />
                            Edit Actual Bill
                        </button>
                    )}

                    <div className="relative" ref={printMenuRef}>
                        <button
                            className="btn btn-primary btn-md flex items-center gap-2"
                            onClick={() => setIsPrintMenuOpen(!isPrintMenuOpen)}
                        >
                            <Printer className="w-4 h-4" />
                            Print
                            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isPrintMenuOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isPrintMenuOpen && (
                            <ul className="absolute right-0 top-full z-[50] menu p-2 shadow-2xl bg-white rounded-2xl w-72 mt-2 border border-slate-100/50 ring-1 ring-black/5 animate-in fade-in zoom-in-95 duration-200">
                                <li className="menu-title px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider">
                                    Print Format
                                </li>
                                <li>
                                    <button
                                        onClick={() => {
                                            setIsPrintMenuOpen(false);
                                            handlePrint('summary');
                                        }}
                                        className="w-full text-left cursor-pointer flex items-center gap-3 p-3 hover:bg-slate-50 focus:bg-slate-50 active:bg-slate-100 rounded-xl transition-all group"
                                    >
                                        <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                                            <FileText className="w-5 h-5" />
                                        </div>
                                        <div className="flex flex-col text-left">
                                            <span className="font-semibold text-slate-900">Summary</span>
                                            <span className="text-xs text-slate-500 font-medium">Standard overview</span>
                                        </div>
                                    </button>
                                </li>
                                <li>
                                    <button
                                        onClick={() => {
                                            setIsPrintMenuOpen(false);
                                            handlePrint('detailed');
                                        }}
                                        className="w-full text-left cursor-pointer flex items-center gap-3 p-3 hover:bg-slate-50 focus:bg-slate-50 active:bg-slate-100 rounded-xl transition-all group"
                                    >
                                        <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                                            <List className="w-5 h-5" />
                                        </div>
                                        <div className="flex flex-col text-left">
                                            <span className="font-semibold text-slate-900">Detailed</span>
                                            <span className="text-xs text-slate-500 font-medium">Includes all line items</span>
                                        </div>
                                    </button>
                                </li>
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            {/* Summary cards (Always visible) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card">
                    <div className="card-body flex items-center gap-4">
                        <div className="w-10 h-10 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center">
                            <User className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500">Patient</p>
                            <p className="font-semibold text-slate-900">{estimate.patientName}</p>
                            {estimate.patientId && (
                                <p className="text-xs text-slate-500">ID: {estimate.patientId}</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body flex items-center gap-4">
                        <div className="w-10 h-10 bg-accent-100 text-accent-600 rounded-lg flex items-center justify-center">
                            <MapPin className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500">Location</p>
                            <p className="font-semibold text-slate-900">{estimate.location?.name}</p>
                            <p className="text-xs text-slate-500">{estimate.location?.city}</p>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body flex items-center gap-4">
                        <div className="w-10 h-10 bg-success-50 text-success-600 rounded-lg flex items-center justify-center">
                            <Calendar className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500">Created</p>
                            <p className="font-semibold text-slate-900">
                                {new Date(estimate.createdAt).toLocaleDateString('en-IN', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                })}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${estimate.status === 'PENDING'
                            ? 'bg-blue-100 text-blue-600'
                            : estimate.status === 'EXPIRED'
                                ? 'bg-gray-100 text-gray-600'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                            <Clock className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500">Status</p>
                            {estimate.status === 'PENDING' && (() => {
                                const daysUntilExpiry = getDaysUntilExpiry(estimate.expiresAt);
                                return (
                                    <>
                                        <p className="font-semibold text-slate-900">
                                            {daysUntilExpiry !== null && daysUntilExpiry > 0
                                                ? `Expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}`
                                                : daysUntilExpiry === 0
                                                    ? 'Expires today'
                                                    : 'Expired'}
                                        </p>
                                        {estimate.expiresAt && (
                                            <p className="text-xs text-slate-500">
                                                {new Date(estimate.expiresAt).toLocaleDateString('en-IN')}
                                            </p>
                                        )}
                                    </>
                                );
                            })()}
                            {estimate.status === 'CONVERTED' && (
                                <p className="font-semibold text-green-600">Surgery completed</p>
                            )}
                            {estimate.status === 'EXPIRED' && (
                                <p className="font-semibold text-gray-600">Estimate expired</p>
                            )}
                            {estimate.status === 'DECLINED' && (
                                <p className="font-semibold text-red-600">Patient declined</p>
                            )}
                            {estimate.status === 'CANCELLED' && (
                                <p className="font-semibold text-orange-600">Cancelled</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs for CONVERTED estimates */}
            {estimate.status === 'CONVERTED' && (
                <div className="border-b border-slate-200">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                        <button
                            onClick={() => setActiveTab('details')}
                            className={`${activeTab === 'details'
                                ? 'border-primary-500 text-primary-600'
                                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
                        >
                            <ClipboardCheck className="w-4 h-4" />
                            Estimate Details
                        </button>
                        <button
                            onClick={() => setActiveTab('variance')}
                            className={`${activeTab === 'variance'
                                ? 'border-primary-500 text-primary-600'
                                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
                        >
                            <BarChart2 className="w-4 h-4" />
                            Variance Analysis
                        </button>
                    </nav>
                </div>
            )}

            {/* Main Content Area */}
            {activeTab === 'variance' && estimate.status === 'CONVERTED' ? (
                <VarianceAnalysis estimate={estimate} />
            ) : (
                /* Original Estimate Details View */
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Cost breakdown */}
                    <div className="lg:col-span-2 card">
                        <div className="card-header">
                            <h2 className="text-lg font-semibold">Cost Breakdown</h2>
                        </div>
                        <div className="card-body space-y-6">
                            {Object.entries(sections)
                                .filter(([, items]) => (items as any[]).some((item) => item.lineTotal > 0))
                                .map(([sectionName, items]) => (
                                <div key={sectionName}>
                                    <h3 className="font-medium text-slate-900 mb-3">{sectionName}</h3>
                                    <div className="space-y-2">
                                        {(items as any[]).filter((item) => item.lineTotal > 0).map((item, idx) => (
                                            <div
                                                key={idx}
                                                className="flex justify-between py-2 border-b border-slate-100 last:border-0"
                                            >
                                                <div>
                                                    <p className="text-slate-700">{item.itemName}</p>
                                                    {item.quantity > 1 && (
                                                        <p className="text-xs text-slate-500">
                                                            {formatCurrency(item.unitPrice)} × {item.quantity}
                                                        </p>
                                                    )}
                                                </div>
                                                <p className="font-medium">{formatCurrency(item.lineTotal)}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            {/* Total */}
                            <div className="pt-4 border-t-2 border-slate-200">
                                <div className="flex justify-between text-lg font-bold">
                                    <span>Total Estimate</span>
                                    <span className="text-primary-600">{formatCurrency(estimate.totalAmount)}</span>
                                </div>
                                {estimate.roomTypeMarkupPercent && estimate.roomTypeMarkupPercent > 0 ? (
                                    <p className="text-xs text-amber-600 mt-2">
                                        * Includes {estimate.roomTypeMarkupPercent}% room type premium (excludes room charges)
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {/* Procedure details */}
                    <div className="card h-fit">
                        <div className="card-header">
                            <h2 className="text-lg font-semibold">Procedure Details</h2>
                        </div>
                        <div className="card-body space-y-4">
                            {estimate.isCustom && (
                                <div className="mb-4">
                                    <span className="badge badge-warning">Custom Estimate</span>
                                </div>
                            )}
                            <div>
                                <p className="text-sm text-slate-500">Procedure</p>
                                <p className="font-medium text-slate-900">
                                    {estimate.isCustom
                                        ? (estimate.customProcedureName || 'Custom Procedure')
                                        : estimate.procedureConfig?.procedure?.name}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Room Type</p>
                                <p className="font-medium text-slate-900">{estimate.roomType}</p>
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Hospital Stay</p>
                                <p className="font-medium text-slate-900">{estimate.stayDays} days</p>
                            </div>
                            {estimate.icuDays > 0 && (
                                <div>
                                    <p className="text-sm text-slate-500">ICU Days</p>
                                    <p className="font-medium text-slate-900">{estimate.icuDays} days</p>
                                </div>
                            )}
                            {estimate.patientConditions?.length > 0 && (() => {
                                // Extract condition names from lines where sectionName contains "Condition"
                                const conditionLines = estimate.lines?.filter((line: any) =>
                                    line.sectionName.toLowerCase().includes('condition')
                                ) || [];

                                return conditionLines.length > 0 ? (
                                    <div>
                                        <p className="text-sm text-slate-500">Patient Conditions</p>
                                        <div className="flex flex-wrap gap-2 mt-1">
                                            {conditionLines.map((line: any) => (
                                                <span key={line.id} className="badge badge-primary">
                                                    {line.itemName}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ) : null;
                            })()}
                            {estimate.estimateDoctors?.length > 0 && (
                                <div>
                                    <p className="text-sm text-slate-500">Doctors</p>
                                    <div className="space-y-1 mt-1">
                                        {estimate.estimateDoctors.map((ed: any) => (
                                            <div key={ed.id} className="flex flex-col">
                                                <span className="font-medium text-slate-900">
                                                    {ed.doctor?.name}
                                                    {ed.isPrimary && <span className="text-xs text-primary-600 ml-2">(Primary)</span>}
                                                </span>
                                                {ed.doctor?.qualification && (
                                                    <span className="text-xs text-slate-500">{ed.doctor.qualification}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Decline Reason or Follow-up Notes (Show on both tabs if present) */}
            {(estimate.declineReason || estimate.followUpNotes) && (
                <div className="card">
                    <div className="card-body">
                        {estimate.declineReason && (
                            <div className="mb-4 last:mb-0">
                                <p className="text-sm font-medium text-slate-700 mb-2">Decline Reason</p>
                                <p className="text-sm text-slate-600 bg-red-50 p-3 rounded-lg border border-red-100">
                                    {estimate.declineReason}
                                </p>
                            </div>
                        )}
                        {estimate.followUpNotes && (
                            <div>
                                <p className="text-sm font-medium text-slate-700 mb-2">Follow-up Notes</p>
                                <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-200">
                                    {estimate.followUpNotes}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Disclaimer */}
            <div className="p-4 bg-slate-100 rounded-lg text-sm text-slate-600">
                <p className="font-medium text-slate-700 mb-1">Disclaimer</p>
                <p>
                    {estimate.location?.hospital?.disclaimerText ||
                        'This is an estimate only. Actual charges may vary based on clinical requirements, complications, and duration of stay. This estimate does not constitute a guarantee of pricing.'}
                </p>
            </div>

            {/* Decline Modal */}
            {showDeclineModal && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                    onClick={() => {
                        setShowDeclineModal(false);
                        setDeclineReason('');
                    }}
                >
                    <div
                        className="bg-white rounded-lg shadow-xl max-w-md w-full animate-fade-in"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6">
                            <div className="mb-6">
                                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                                    Decline Estimate
                                </h3>
                                <p className="text-sm text-slate-600">
                                    Declining estimate for <span className="font-medium text-slate-900">{estimate.patientName}</span>
                                </p>
                            </div>

                            <div className="mb-6">
                                <label className="label">
                                    Reason (Optional)
                                </label>
                                <textarea
                                    value={declineReason}
                                    onChange={(e) => setDeclineReason(e.target.value)}
                                    placeholder="Enter reason for declining..."
                                    className="input h-32 resize-none"
                                    rows={4}
                                    autoFocus
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                    This information will be saved for future reference
                                </p>
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => {
                                        setShowDeclineModal(false);
                                        setDeclineReason('');
                                    }}
                                    className="btn btn-secondary btn-md"
                                    disabled={updateStatusMutation.isPending}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeclineSubmit}
                                    className="btn btn-primary btn-md"
                                    disabled={updateStatusMutation.isPending}
                                >
                                    {updateStatusMutation.isPending ? 'Declining...' : 'Decline Estimate'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}



            {/* Hidden printable version */}
            <div ref={printableRef} className="print-only">
                <PrintableEstimate
                    estimate={estimate}
                    topSpacing={printTopSpacing}
                    bottomSpacing={printBottomSpacing}
                    detailed={printMode === 'detailed'}
                />
            </div>

            <style>{`
                @media screen {
                    .print-only {
                        display: none;
                    }
                }
            `}</style>
        </div>
    );
}
